import { Injectable, OnDestroy } from '@angular/core';
import { GithubService } from 'app/api/services';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { interval, Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { marked } from 'marked';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';
import { GenericInfoDialogService } from './generic-info-dialog/infoDialog.service';

@Injectable({providedIn: 'root'})
export class ReleaseCheckService implements OnDestroy
{
    private destroy$ = new Subject<void>();
    private readonly CHECK_INTERVAL = 3 * 60 * 1000; // 3 minutes
    private isChecking = false;

    constructor(
        private githubService: GithubService,
        public _infoDialogService: GenericInfoDialogService,
        private appAuth: AppAuthService
    ) {}

    startChecking(): void {
        if (this.isChecking) {
            return; // Prevent multiple polling instances
        }

        this.isChecking = true;

        // Check immediately on startup
        this.checkForNewRelease();

        // Then poll every 3 minutes
        interval(this.CHECK_INTERVAL)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.githubService.apiGithubGetLatestReleaseNoteGet$Json$Response())
            )
            .subscribe({
                next: (res) => {
                    if (!res?.body?.data) return;

                    const version = res.body.data;
                    const user = this.appAuth.getUser();

                    if (!user?.id) return;

                    // Check if user has seen this release
                    this.githubService.apiGithubHasUserSeenReleaseGet$Json$Response({
                        userId: +user.id,
                        latestReleaseName: version.name
                    }).subscribe({
                        next: (seenRes) => {
                            if (!seenRes?.body) return;

                            const seen = seenRes.body.data;

                            if (seen) return; // Already seen this release

                            // New release detected! Show dialog
                            this.showReleaseDialog(version);
                        },
                        error: (err) => console.error('Error checking if release seen:', err)
                    });
                },
                error: (err) => console.error('Error fetching latest release:', err)
            });
    }

    private checkForNewRelease(): void {
        const user = this.appAuth.getUser();

        if (!user?.id) {
            return;
        }

        this.githubService.apiGithubGetLatestReleaseNoteGet$Json$Response().subscribe({
            next: (res) => {
                if (!res?.body?.data) return;

                const version = res.body.data;

                // Check if user has seen this release
                this.githubService.apiGithubHasUserSeenReleaseGet$Json$Response({
                    userId: +user.id,
                    latestReleaseName: version.name
                }).subscribe({
                    next: (seenRes) => {
                        if (!seenRes?.body) return;

                        const seen = seenRes.body.data;

                        if (seen) return; // Already seen this release

                        // New release detected! Show dialog
                        this.showReleaseDialog(version);
                    },
                    error: (err) => console.error('Error checking if release seen:', err)
                });
            },
            error: (err) => console.error('Error fetching latest release:', err)
        });
    }

    private showReleaseDialog(version: any): void {
        const truncatedBody = version.body?.split('---')[0];

        const dialog = this._infoDialogService.open({
            title: 'App Has Been Updated!',
            message: `The Nexus app has been updated to ${version.name}.<br/><br/><div class="release-note">${this.translateMarkdown(truncatedBody)}</div>`,
            actions: {
                confirm: {
                    label: 'OK',
                },
                cancel: {
                    show: false,
                },
            },
            icon: {
                show: true,
                name: 'heroicons_outline:information-circle',
                color: 'info',
            }
        });

        // Force refresh when dialog closes
        dialog.afterClosed().subscribe(() => {
            window.location.reload();
        });
    }

    private translateMarkdown(body: string): string {
        if (!body) return '';

        let parsed = <string>marked.parse(body, {async: false});

        parsed = parsed.replaceAll('</h1>', '</div>');
        parsed = parsed.replaceAll('<h1>', '<div class="text-3xl border-b py-2 mb-8">');
        parsed = parsed.replaceAll('</h2>', '</div>');
        parsed = parsed.replaceAll('<h2>', '<div class="text-2xl border-b py-1 mt-4 mb-2">');

        parsed = parsed.replaceAll('<ul>', '<ul class="pt-1 pb-2 ml-6">');

        parsed = parsed.replaceAll('<hr>', '<div class="border-b mt-8 mb-4"></div>');

        return parsed;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
